/**
 * リアルタイムテスト WebSocket 接続カスタムフック
 *
 * Question Backend CDK の AppSync Events に接続し、
 * 問題の配信・締切・解説終了などのイベントをリアルタイムで受信する。
 * チャット (useRealtimeChat) と同じ AppSync Events パターン。
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { Question } from '../types/test';
import { ensureFrontendSettings } from '../api/frontendSettings';
import { MAX_BACKOFF_EXPONENT, backoffDelayMs, DEFAULT_KA_TIMEOUT_MS } from '../utils/wsBackoff';
import {
  getQuestionConfig,
  publishAnswer,
  mapBackendStatus,
  mapBackendQuestion,
  type QuestionEventPayload,
  type SubmitAnswerData,
  type RankingEntry,
} from '../api/realtimeQuestion';

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// =============================================================================
// 型定義
// =============================================================================

export interface QuestionOpenedEvent {
  questionId: number;
  displayNumber: number | null;
  question: Question;
  scheduledCloseTime: number;
  /** キャッチアップ復元時のみ付与される集計データ */
  aggregation?: {
    choiceCounts: Record<string, number>;
    totalAnswers: number;
    correctCount: number;
  };
}

export interface ClosedChoiceInfo {
  num: number;
  text: string;
  is_correct: boolean;
  description?: string;
}

export interface QuestionClosedEvent {
  questionId: number;
  correctChoice: number;
  totalAnswers: number;
  correctCount: number;
  ranking: RankingEntry[];
  /** CDK が返す正解付き選択肢データ */
  choices: ClosedChoiceInfo[];
  explanation: string;
  /** 選択肢ごとの回答数 (例: { choice_1: 5, choice_2: 10 }) */
  choiceCounts: Record<string, number>;
  /** 解法のポイント */
  helpText?: string;
}

export interface UseRealtimeQuestionOptions {
  tenantId: string;
  lectureId: string;
  enabled?: boolean;
  userId?: string;
  onQuestionOpened?: (event: QuestionOpenedEvent) => void;
  onQuestionClosed?: (event: QuestionClosedEvent) => void;
  onExplanationEnded?: (questionId: number) => void;
  onTestCancelled?: (questionId: number) => void;
  onAnswerAccepted?: (questionId: number) => void;
  /** チャンネル購読成功時（初回・再接続とも）に呼ばれる。切断中のイベント取りこぼしを再同期する契機 */
  onSubscribeSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseRealtimeQuestionReturn {
  isConnected: boolean;
  error: string | null;
  submitAnswer: (data: Omit<SubmitAnswerData, 'lecture_id'>, options?: { signal?: AbortSignal }) => Promise<void>;
  connect: () => void;
  disconnect: () => void;
}

// =============================================================================
// フック本体
// =============================================================================

export function useRealtimeQuestion(
  options: UseRealtimeQuestionOptions,
): UseRealtimeQuestionReturn {
  const {
    tenantId,
    lectureId,
    enabled = true,
    userId,
    onQuestionOpened,
    onQuestionClosed,
    onExplanationEnded,
    onTestCancelled,
    onAnswerAccepted,
    onSubscribeSuccess,
    onError,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  // keep-alive（ka）途絶検知用（STU-H3対応）
  const kaWatchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const kaTimeoutMsRef = useRef(DEFAULT_KA_TIMEOUT_MS);

  // コールバックを最新で保持（再接続時にも最新のコールバックが使われるように）
  const callbacksRef = useRef({
    onQuestionOpened,
    onQuestionClosed,
    onExplanationEnded,
    onTestCancelled,
    onAnswerAccepted,
    onSubscribeSuccess,
    onError,
  });
  callbacksRef.current = {
    onQuestionOpened,
    onQuestionClosed,
    onExplanationEnded,
    onTestCancelled,
    onAnswerAccepted,
    onSubscribeSuccess,
    onError,
  };

  // enabledのRefバージョン。connectはuseCallbackの依存配列にenabledを含めないため、
  // onclose内でクロージャの古いenabled値（初回レンダリング時のfalse等）を参照すると
  // 再接続がスケジュールされなくなる。常に最新値を参照するためRefを経由する
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // ------------------------------------------------------------------
  // 途中参加・再接続時の再同期は LecturePage 側の syncCurrentQuestion が担う。
  // 本フックは subscribe_success を onSubscribeSuccess で通知するのみ
  // （復元ロジックの二重実装を避けるため。詳細: _my/バグ修正0703/04_受講生再接続再同期_設計書.md）
  // ------------------------------------------------------------------

  // ------------------------------------------------------------------
  // WebSocket 接続
  // ------------------------------------------------------------------
  const connect = useCallback(async () => {
    if (!isMountedRef.current) return;
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // userIdが空または無効な場合は接続しない（認証完了まで待つ）
    if (!userId || userId === 'anonymous' || userId.trim() === '') {
      console.warn('[RealtimeQuestion] userId is invalid or empty, skipping connection:', userId);
      return;
    }

    // 設定を取得（frontend-settings API。取得失敗時はエラー表示）
    try {
      await ensureFrontendSettings();
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : '設定の取得に失敗しました';
      setError(errorMsg);
      // 接続系エラーとして分類する（再接続ループ中に繰り返し発生するため、
      // 呼び出し側でモーダル表示を抑制できるようにする。STU-7対応）
      const connectionError = new Error(errorMsg);
      connectionError.name = 'ConnectionError';
      callbacksRef.current.onError?.(connectionError);
      // WebSocket生成前の失敗（オフライン中の設定取得失敗など）はoncloseが発火しないため、
      // ここで再試行をスケジュールしないと再接続ループが停止する
      if (isMountedRef.current && enabledRef.current) {
        reconnectAttemptsRef.current = Math.min(reconnectAttemptsRef.current + 1, MAX_BACKOFF_EXPONENT);
        const delay = backoffDelayMs(reconnectAttemptsRef.current);
        console.log(`[Question] ${delay}ms後に接続を再試行します (試行 ${reconnectAttemptsRef.current})`);
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
      return;
    }

    // 取得待ちの間に接続・アンマウントが進んでいたら中断
    if (!isMountedRef.current ||
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const config = getQuestionConfig();

    if (!config.realtimeEndpoint || !config.apiKey) {
      const errorMsg = 'テスト設定が不完全です。管理画面のAppSync設定を確認してください';
      setError(errorMsg);
      callbacksRef.current.onError?.(new Error(errorMsg));
      return;
    }

    const authorization = {
      Authorization: config.apiKey,
      host: config.httpEndpoint,
      userType: 'student',
      userId, // 'anonymous'フォールバックを削除
    };

    const authHeader = btoa(JSON.stringify(authorization))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    // 破棄しきれていない古いソケット（CLOSING中など）が残っていれば完全に破棄する。
    // 古いソケットのoncloseが新しい接続のwsRefをnull化して再接続を誘発すると、
    // 購読済みソケットが参照を失ったまま残り（ゾンビ接続）イベントが多重受信される
    if (wsRef.current) {
      const staleWs = wsRef.current;
      staleWs.onopen = null;
      staleWs.onmessage = null;
      staleWs.onerror = null;
      staleWs.onclose = null;
      if (staleWs.readyState === WebSocket.CONNECTING || staleWs.readyState === WebSocket.OPEN) {
        staleWs.close();
      }
      wsRef.current = null;
    }

    const ws = new WebSocket(config.realtimeEndpoint, [
      'aws-appsync-event-ws',
      `header-${authHeader}`,
    ]);
    wsRef.current = ws;

    // ------------------------------------------------------------------
    // keep-alive（ka）途絶の検知（STU-H3対応）
    // モバイル回線等では TCP 接続が無音のまま死に、onclose も offline イベントも
    // 発火しない「ゾンビ接続」が発生する。AppSync は接続維持中 ka を定期送信するため、
    // connectionTimeoutMs の間なにも受信しなければ接続が死んだと判断し、
    // 死んだソケットを即座に破棄して再接続する（close 完了の onclose は待たない）
    // ------------------------------------------------------------------
    const clearKaWatchdog = () => {
      if (kaWatchdogTimerRef.current) {
        clearTimeout(kaWatchdogTimerRef.current);
        kaWatchdogTimerRef.current = null;
      }
    };
    const resetKaWatchdog = () => {
      clearKaWatchdog();
      kaWatchdogTimerRef.current = setTimeout(() => {
        if (wsRef.current !== ws) return;
        console.warn('[Question] keep-alive途絶を検知（ゾンビ接続）、再接続します');
        setIsConnected(false);
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        wsRef.current = null;
        try {
          ws.close(4001, 'Keep-alive timeout');
        } catch {
          // すでに閉じている場合は無視
        }
        if (isMountedRef.current && enabledRef.current) {
          connect();
        }
      }, kaTimeoutMsRef.current);
    };

    ws.onopen = () => {
      // 既に新しいソケットに置き換えられている場合は自分を閉じて終了（ゾンビ接続防止）
      if (wsRef.current !== ws) {
        ws.close();
        return;
      }
      if (!isMountedRef.current) {
        ws.close();
        return;
      }
      ws.send(JSON.stringify({ type: 'connection_init' }));
    };

    ws.onmessage = (event) => {
      // 置き換え済みの古いソケットからの受信は無視（イベント多重受信防止）
      if (wsRef.current !== ws) return;
      // 受信（ka含む）があった＝接続は生きているため、途絶検知タイマーをリセット
      resetKaWatchdog();
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'connection_ack') {
          // AppSync が通知するアイドルタイムアウトを ka 途絶検知のしきい値に使う
          if (typeof data.connectionTimeoutMs === 'number' && data.connectionTimeoutMs > 0) {
            kaTimeoutMsRef.current = data.connectionTimeoutMs;
            resetKaWatchdog();
          }
          setIsConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;

          const channelPath = `question/${tenantId}/${lectureId}/messages`;
          ws.send(
            JSON.stringify({
              type: 'subscribe',
              id: generateUUID(),
              channel: channelPath,
              authorization: {
                Authorization: config.apiKey,
                host: config.httpEndpoint,
                userType: 'student',
                userId, // 'anonymous'フォールバックを削除（接続時にuserIdチェック済み）
              },
            }),
          );
          return;
        }

        if (data.type === 'ka') return;

        if (data.type === 'connection_error') {
          console.error('[Question] 接続エラー:', data.errors);
          return;
        }

        if (data.type === 'subscribe_success') {
          console.log('[Question] チャンネル購読成功');
          // 初回・再接続とも通知し、切断中に取りこぼしたイベントを再同期させる
          callbacksRef.current.onSubscribeSuccess?.();
          return;
        }

        if (data.type === 'subscribe_error') {
          console.error('[Question] サブスクライブエラー:', data);
          return;
        }

        // データイベント処理
        if (data.type === 'data' && data.event) {
          let payload: QuestionEventPayload;

          if (typeof data.event === 'string') {
            const firstParse = JSON.parse(data.event);
            payload = typeof firstParse === 'string' ? JSON.parse(firstParse) : firstParse;
          } else {
            payload = data.event;
          }

          handleQuestionEvent(payload);
        }
      } catch (err) {
        console.error('[Question] メッセージパースエラー:', err);
      }
    };

    ws.onerror = () => {
      // 置き換え済みの古いソケットのイベントは無視
      if (wsRef.current !== ws) return;
      if (!isMountedRef.current) return;
      setError('WebSocket接続エラーが発生しました');
      // 接続系エラーとして分類する（切断中は再接続試行のたびに発生するため、
      // 呼び出し側でモーダル表示を抑制できるようにする。STU-7対応）
      const connectionError = new Error('WebSocket error');
      connectionError.name = 'ConnectionError';
      callbacksRef.current.onError?.(connectionError);
    };

    ws.onclose = (closeEvent) => {
      // 置き換え済みの古いソケットのoncloseは無視する。
      // ここでwsRefをnull化・再接続すると、生きている新しいソケットが
      // 参照を失ったまま残り（ゾンビ接続）イベントが多重受信される
      if (wsRef.current !== ws) return;
      clearKaWatchdog();
      if (!isMountedRef.current) {
        setIsConnected(false);
        wsRef.current = null;
        return;
      }

      console.log(`[Question] WebSocket切断: code=${closeEvent.code}`);
      setIsConnected(false);
      wsRef.current = null;

      // 無期限に再接続を試行する（指数バックオフ + ジッタ、上限30秒。STU-H2対応）
      // 試行回数での打ち切りは行わない（長時間切断後もリロードなしで復帰させるため）
      if (enabledRef.current) {
        reconnectAttemptsRef.current = Math.min(reconnectAttemptsRef.current + 1, MAX_BACKOFF_EXPONENT);
        const delay = backoffDelayMs(reconnectAttemptsRef.current);
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };
    // enabledはRef経由で参照するため依存配列から除外（無限ループを防ぐため）
  }, [tenantId, lectureId, userId]);

  // ------------------------------------------------------------------
  // イベントハンドラ
  // ------------------------------------------------------------------
  /**
   * イベントから question_id を取得する。
   * フラット構造（event.question_id）とネスト構造（event.payload.question_id）の両方に対応。
   * 後方互換性のため question_num も確認する。
   */
  function extractQuestionId(event: QuestionEventPayload): number {
    return event.payload?.question_id as number
      ?? event.question_id
      ?? event.questionId
      ?? event.payload?.question_num as number
      ?? event.question_num
      ?? event.questionNum
      ?? 0;
  }

  function handleQuestionEvent(event: QuestionEventPayload) {
    const { eventType } = event;
    const questionId = extractQuestionId(event);

    switch (eventType) {
      case 'QuestionOpened': {
        const questionData = event.question ?? event.payload?.question;
        const displayNumber = event.display_number ?? event.payload?.display_number ?? null;
        const statusStr = event.status ?? event.payload?.status ?? 'answering';
        console.log('[Question][handleQuestionEvent] QuestionOpened:', { 
          questionId, 
          displayNumber, 
          format_type: (questionData as any)?.format_type,
          eventDisplayNumber: event.display_number,
          payloadDisplayNumber: event.payload?.display_number 
        });
        if (questionData) {
          const status = mapBackendStatus(statusStr as string);
          const question = mapBackendQuestion(questionData as any, questionId, status, false, displayNumber);
          callbacksRef.current.onQuestionOpened?.({
            questionId,
            displayNumber,
            question,
            scheduledCloseTime: event.scheduled_close_time
              ?? (event.payload?.scheduled_close_time as number)
              ?? 0,
          });
        }
        break;
      }

      case 'QuestionClosed': {
        const questionData = (event.question ?? event.payload?.question) as any;
        const closedChoices: ClosedChoiceInfo[] = questionData?.choices ?? [];
        const explanation = questionData?.explanation ?? '';
        const helpText = questionData?.help_text ?? questionData?.helpText ?? '';
        const correctChoice = questionData?.correct_choice
          ?? (event.correct_choice as number)
          ?? (event.payload?.correct_choice as number)
          ?? 0;

        callbacksRef.current.onQuestionClosed?.({
          questionId,
          correctChoice,
          totalAnswers: (event.total_answers ?? event.payload?.total_answers ?? 0) as number,
          correctCount: (event.correct_count ?? event.payload?.correct_count ?? 0) as number,
          ranking: (event.ranking ?? event.payload?.ranking ?? []) as RankingEntry[],
          choices: closedChoices,
          explanation,
          choiceCounts: (event.choice_counts ?? event.payload?.choice_counts ?? {}) as Record<string, number>,
          helpText,
        });
        break;
      }

      case 'ExplanationEnded': {
        callbacksRef.current.onExplanationEnded?.(questionId);
        break;
      }

      case 'TestCancelled': {
        callbacksRef.current.onTestCancelled?.(questionId);
        break;
      }

      case 'AnswerReceived': {
        callbacksRef.current.onAnswerAccepted?.(questionId);
        break;
      }

      case 'AnswerRejected': {
        const reason = event.reason ?? event.payload?.reason;
        console.warn('[Question] 回答が拒否されました:', reason);

        // エラーメッセージを設定
        let errorMessage = '解答が受け付けられませんでした。';
        if (reason === 'Duplicate answer') {
          errorMessage = '既に解答済みです。';
        } else if (reason === 'Question is closed') {
          errorMessage = '解答時間を過ぎています。';
        }

        callbacksRef.current.onError?.(new Error(errorMessage));
        break;
      }

      default:
        console.log('[Question] 未処理のイベント:', eventType, event);
    }
  }

  // ------------------------------------------------------------------
  // WebSocket 切断
  // ------------------------------------------------------------------
  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (kaWatchdogTimerRef.current) {
      clearTimeout(kaWatchdogTimerRef.current);
      kaWatchdogTimerRef.current = null;
    }

    if (wsRef.current) {
      const ws = wsRef.current;
      ws.onerror = null;
      ws.onclose = null;
      ws.onopen = null;
      ws.onmessage = null;

      if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      wsRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // ------------------------------------------------------------------
  // 回答送信
  // ------------------------------------------------------------------
  const submitAnswerFn = useCallback(
    async (data: Omit<SubmitAnswerData, 'lecture_id'>, options?: { signal?: AbortSignal }) => {
      // 回答は回答受付専用の answers ネームスペースへ publish する（BP-4対応）。
      // イベント購読・Heartbeat は従来どおり question ネームスペースを使用する
      // （回答バーストとイベント配信の Lambda コンカレンシー分離のため、
      //   バックエンド側で onPublishAnswer Lambda が answers ネームスペースを担当）
      const channelPath = `answers/${tenantId}/${lectureId}/messages`;

      await publishAnswer(channelPath, {
        ...data,
        lecture_id: lectureId,
      }, options);
    },
    [tenantId, lectureId],
  );

  // ------------------------------------------------------------------
  // ライフサイクル
  // ------------------------------------------------------------------
  useEffect(() => {
    isMountedRef.current = true;

    console.log('[useRealtimeQuestion][DEBUG] useEffect実行:', {
      enabled,
      tenantId,
      lectureId,
      userId,
      shouldConnect: enabled && !!tenantId && !!lectureId && !!userId && userId !== 'anonymous',
    });

    // userIdが有効な場合のみ接続を試みる（空文字、'anonymous'、空白文字のみの場合は接続しない）
    if (!enabled || !tenantId || !lectureId || !userId || userId === 'anonymous' || userId.trim() === '') {
      console.log('[useRealtimeQuestion][DEBUG] 接続条件を満たさない、接続しない');
      return;
    }

    console.log('[useRealtimeQuestion][DEBUG] 接続条件を満たす、connect()を呼び出し');
    connect();

    return () => {
      isMountedRef.current = false;
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId, lectureId, userId]);

  // ------------------------------------------------------------------
  // ネットワーク復帰・タブ復帰時の即時再接続
  // （モバイルはバックグラウンドで OS が WebSocket を切断するため、
  //   バックオフ待ちをスキップして即座に接続を回復させる）
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;

    const reconnectNow = () => {
      if (!isMountedRef.current) return;
      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }
      // 待機中のバックオフタイマーを破棄して即時接続
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      console.log('[Question] ネットワーク/タブ復帰を検知、即時再接続');
      connect();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reconnectNow();
    };

    // ネットワーク切断の即時検知（offlineイベント）
    // Wi-Fiオフ等ではOSがTCP切断を通知せず、oncloseがTCPタイムアウトまで
    // 数分間発火しない（ゾンビ接続）ことがあるため、明示的にソケットを閉じて
    // onclose経由で切断状態への更新と再接続ループへの移行を行わせる
    const onOffline = () => {
      if (!isMountedRef.current) return;
      const ws = wsRef.current;
      if (!ws) return;
      console.log('[Question] ネットワーク切断を検知（offlineイベント）');
      setIsConnected(false);
      ws.close(4000, 'Network offline');
    };

    window.addEventListener('online', reconnectNow);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('online', reconnectNow);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, connect]);

  // ------------------------------------------------------------------
  // Heartbeat 送信（2分ごと）
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isConnected || !tenantId || !lectureId || !userId) {
      return;
    }

    const sendHeartbeat = () => {
      // WebSocket経由でheartbeatメッセージを送信
      // AppSync Events WebSocketのpublishメッセージには authorization フィールドが必須
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const config = getQuestionConfig();
        
        const heartbeatEvent = {
          eventType: 'Heartbeat',
          payload: {
            tenant_id: tenantId,
            lecture_id: lectureId,
            user_id: userId,
          },
        };

        // ユニークなID生成: userId + タイムスタンプ + ランダム値で重複を防ぐ
        const uniqueId = `heartbeat-${userId}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

        const publishMessage = {
          id: uniqueId,
          type: 'publish',
          channel: `question/${tenantId}/${lectureId}/messages`,
          events: [JSON.stringify(heartbeatEvent)],
          authorization: {
            Authorization: config.apiKey,
            host: config.httpEndpoint,
          },
        };

        wsRef.current.send(JSON.stringify(publishMessage));
        console.log('[RealtimeQuestion] Heartbeat sent via WebSocket');
      } else {
        console.warn('[RealtimeQuestion] WebSocket not open, skipping heartbeat');
      }
    };

    // 初回送信（接続直後、少し遅延）
    const initialTimeout = setTimeout(() => {
      sendHeartbeat();
    }, 3000); // 3秒後

    // 2分ごとに送信
    const interval = setInterval(sendHeartbeat, 120000); // 120,000ms = 2分

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isConnected, tenantId, lectureId, userId]);

  return {
    isConnected,
    error,
    submitAnswer: submitAnswerFn,
    connect,
    disconnect,
  };
}
